import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { BarChart3, RefreshCw, Zap, Database } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPricesTab = ({ token }) => {
  const apiCall = async (url, successMsg) => {
    try {
      const res = await fetch(`${API_URL}${url}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) toast.success(successMsg(data));
      else toast.error(data.message || 'Operation failed');
    } catch { toast.error('Error performing operation'); }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800 mb-8" data-testid="admin-prices-tab">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-6 w-6 text-blue-500" />
            <h3 className="text-lg font-semibold text-white">Price Management</h3>
          </div>
        </div>

        <div className="space-y-6">
          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <h4 className="text-blue-300 font-medium mb-2">About Instrument Prices</h4>
            <p className="text-slate-300 text-sm">
              Instrument prices are fetched from real market APIs (Yahoo Finance, CoinGecko). 
              The <strong>week_open</strong> price is set at the start of each week and used to calculate percentage changes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-emerald-400" /> Refresh Market Prices
              </h4>
              <p className="text-slate-400 text-sm mb-4">Fetch latest prices from Yahoo Finance and CoinGecko.</p>
              <Button
                onClick={() => {
                  toast.info('Refreshing prices...');
                  apiCall('/api/admin/prices/refresh', (d) => `Updated ${d.updated} instruments`);
                }}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid="refresh-prices-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh Prices
              </Button>
            </div>

            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-400" /> Initialize Week Open
              </h4>
              <p className="text-slate-400 text-sm mb-4">Sets week_open = current_price for all instruments.</p>
              <Button
                onClick={() => {
                  toast.info('Initializing week prices...');
                  apiCall('/api/admin/instruments/init-week-open', (d) => `Initialized week_open for ${d.updated} instruments`);
                }}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="init-week-btn"
              >
                <BarChart3 className="h-4 w-4 mr-2" /> Initialize Week
              </Button>
            </div>

            <div className="p-4 bg-slate-800/50 rounded-lg border border-emerald-700/50">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <Database className="h-4 w-4 text-emerald-400" /> Seed Expanded Instruments
              </h4>
              <p className="text-slate-400 text-sm mb-4">Adds 267 instruments (220 stocks, 10 crypto, 10 ETFs, 10 commodities). Safe to run multiple times.</p>
              <Button
                onClick={() => {
                  if (!confirm('This will add ~207 new instruments to the database. Existing instruments will be updated. Continue?')) return;
                  toast.info('Seeding expanded instruments...');
                  apiCall('/api/admin/instruments/seed-expanded', (d) => `Done! ${d.new_instruments_added} new instruments added (${d.instruments_after} total)`);
                }}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid="seed-expanded-btn"
              >
                <Database className="h-4 w-4 mr-2" /> Seed 267 Instruments
              </Button>
            </div>

            <div className="p-4 bg-slate-800/50 rounded-lg border border-amber-700/50 md:col-span-2">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-400" /> Simulate Price Changes (Demo/Testing)
              </h4>
              <p className="text-slate-400 text-sm mb-4">
                <strong className="text-amber-400">For demo purposes only!</strong> Artificially adjusts week_open prices so instruments show +/- changes.
              </p>
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => {
                    if (!confirm('This will simulate price changes for demo purposes. Continue?')) return;
                    toast.info('Simulating price changes...');
                    apiCall('/api/admin/prices/simulate-week-change?variance_pct=5', (d) => `Simulated changes for ${d.updated} instruments!`);
                  }}
                  variant="outline"
                  className="border-amber-600 text-amber-400 hover:bg-amber-600/20"
                  data-testid="simulate-prices-btn"
                >
                  <Zap className="h-4 w-4 mr-2" /> Simulate +/- 5% Changes
                </Button>
                <span className="text-slate-500 text-sm">Creates realistic-looking price movements</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <h4 className="text-emerald-400 font-medium mb-2">Troubleshooting</h4>
            <div className="space-y-2 text-slate-300 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-yellow-400">&#x2022;</span>
                <p><strong>All instruments show 0%:</strong> Click "Simulate +/- 5% Changes" for demo purposes.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-yellow-400">&#x2022;</span>
                <p><strong>Prices not updating:</strong> Click "Refresh Prices" to fetch latest data.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-yellow-400">&#x2022;</span>
                <p><strong>Week started, prices reset:</strong> Week_open is automatically set on Mondays.</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
